/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Driver } from 'driver.js';
import { createRoot, type Root } from 'react-dom/client';

import axios from '../../api/axios';
import { getApplicationCodeForApi } from '../../config/app.config';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import { resolveCoreApiPath } from '../../utils/coreAssetUrl';
import { Icon } from './ComponentIcon';
import {
  listRegisteredProductTours,
  subscribeProductTourRegistry,
} from './product-tour.registry';
import { requestProductTourSurface } from './product-tour.surface';
import {
  escapeProductTourText,
  isProductTourApplicationCode,
  isProductTourStepAuthorized,
  isVisibleProductTourElement,
  parseProductTourAccountState,
  productTourSettingKey,
  productTourTargetSelector,
  resolveProductTourAlignmentForDirection,
  shouldAutoStartProductTour,
  type ProductTourAccountState,
  type ProductTourDefinition,
  type ProductTourRevealSurface,
  type ProductTourStateStatus,
} from './product-tour.utils';
import './Modal.css';
import './FormModal.css';
import './ProductTourHost.css';

export interface ProductTourHostHandle {
  start: () => Promise<boolean>;
}

export type ProductTourLaunchMode = 'start' | 'restart';

export interface ProductTourHostProps {
  onLaunchModeChange?: (mode: ProductTourLaunchMode | null) => void;
}

type ProductTourLabels = {
  navigationTitle: string;
  navigationDescription: string;
  appsTitle: string;
  appsDescription: string;
  profileTitle: string;
  profileDescription: string;
  aiTitle: string;
  aiDescription: string;
  helpTitle: string;
  helpDescription: string;
  next: string;
  previous: string;
  done: string;
  close: string;
  neverShowAgain: string;
  progress: string;
};

const AUTO_START_DELAY_MS = 900;
const PRODUCT_TOUR_LABEL_KEYS: readonly (keyof ProductTourLabels)[] = [
  'navigationTitle',
  'navigationDescription',
  'appsTitle',
  'appsDescription',
  'profileTitle',
  'profileDescription',
  'aiTitle',
  'aiDescription',
  'helpTitle',
  'helpDescription',
  'next',
  'previous',
  'done',
  'close',
  'neverShowAgain',
  'progress',
];

function resolveProductTourLabels(value: unknown): ProductTourLabels | null {
  const labels = value && typeof value === 'object' ? value as Partial<ProductTourLabels> : {};
  const complete = PRODUCT_TOUR_LABEL_KEYS.every((key) => (
    typeof labels[key] === 'string' && Boolean(labels[key]?.trim())
  ));
  return complete ? labels as ProductTourLabels : null;
}

function buildFallbackDefinition(applicationCode: string, labels: ProductTourLabels): ProductTourDefinition {
  return {
    id: 'shell-overview',
    applicationCode,
    version: 2,
    priority: -100,
    autoStart: true,
    steps: [
      {
        target: 'primary-navigation',
        title: labels.navigationTitle,
        description: labels.navigationDescription,
        side: 'right',
        align: 'start',
      },
      {
        target: 'app-launcher',
        title: labels.appsTitle,
        description: labels.appsDescription,
        side: 'bottom',
        align: 'end',
      },
      {
        target: 'user-profile',
        title: labels.profileTitle,
        description: labels.profileDescription,
        side: 'bottom',
        align: 'end',
      },
      {
        target: 'ai-assistant',
        title: labels.aiTitle,
        description: labels.aiDescription,
        side: 'bottom',
        align: 'end',
      },
      {
        target: 'help-center',
        title: labels.helpTitle,
        description: labels.helpDescription,
        side: 'bottom',
        align: 'end',
      },
    ],
  };
}

function prepareProductTourSurface(surface: ProductTourRevealSurface | undefined): Promise<void> {
  requestProductTourSurface(surface ?? null);
  return new Promise((resolve) => window.setTimeout(resolve, 0));
}

function readSettingValue(payload: unknown): unknown {
  if (!payload || typeof payload !== 'object') return undefined;
  const body = payload as { data?: unknown; value?: unknown };
  if (body.data && typeof body.data === 'object' && 'value' in body.data) {
    return (body.data as { value?: unknown }).value;
  }
  return body.value ?? body.data;
}

function resolveOverlayColor(): string | undefined {
  const value = window.getComputedStyle(document.documentElement)
    .getPropertyValue('--overlay-scrim')
    .trim();
  return value || undefined;
}

export const ProductTourHost = forwardRef<ProductTourHostHandle, ProductTourHostProps>(function ProductTourHost({
  onLaunchModeChange,
}, ref) {
  const { user, patchUserSettings } = useAuth();
  const t = useTranslation();
  const applicationCode = getApplicationCodeForApi();
  const applicationCodeReady = isProductTourApplicationCode(applicationCode);
  const common = t.common as unknown as { productTour?: Partial<ProductTourLabels> };
  const labels = useMemo(
    () => resolveProductTourLabels(common.productTour),
    [common.productTour],
  );
  const fallbackDefinition = useMemo(
    () => labels && applicationCodeReady
      ? buildFallbackDefinition(applicationCode, labels)
      : null,
    [applicationCode, applicationCodeReady, labels],
  );
  const [registryRevision, setRegistryRevision] = useState(0);
  const [state, setState] = useState<ProductTourAccountState | null>(null);
  const [stateReady, setStateReady] = useState(false);
  const [stateLoadFailed, setStateLoadFailed] = useState(false);
  const activeDriverRef = useRef<Driver | null>(null);
  const closeIconMountRef = useRef<{ container: HTMLElement; root: Root } | null>(null);
  const outcomeHandledRef = useRef(false);
  const startNonceRef = useRef(0);

  // Driver owns the popover DOM. Defer nested React-root disposal so a Driver
  // destroy/HMR cleanup cannot synchronously unmount while the host is rendering.
  const releaseCloseIconRoot = useCallback(() => {
    const mount = closeIconMountRef.current;
    closeIconMountRef.current = null;
    if (!mount) return;
    queueMicrotask(() => mount.root.unmount());
  }, []);

  useEffect(() => subscribeProductTourRegistry(() => setRegistryRevision((value) => value + 1)), []);

  const definition = useMemo(() => {
    void registryRevision;
    return applicationCodeReady
      ? listRegisteredProductTours(applicationCode)[0] ?? fallbackDefinition
      : null;
  }, [applicationCode, applicationCodeReady, fallbackDefinition, registryRevision]);
  const settingKey = useMemo(() => definition ? productTourSettingKey(definition) : null, [definition]);

  useEffect(() => {
    if (!definition) {
      onLaunchModeChange?.(null);
      return;
    }
    if (!stateReady || stateLoadFailed) {
      onLaunchModeChange?.('restart');
      return;
    }
    onLaunchModeChange?.(state ? 'restart' : 'start');
  }, [definition, onLaunchModeChange, state, stateLoadFailed, stateReady]);

  useEffect(() => {
    let cancelled = false;
    setStateReady(false);
    setStateLoadFailed(false);
    setState(null);
    if (!user?.id || !settingKey) return () => { cancelled = true; };

    void axios
      .get(resolveCoreApiPath(`/api/user-settings/${encodeURIComponent(settingKey)}`))
      .then((response) => {
        if (cancelled) return;
        const raw = readSettingValue(response.data);
        if (raw !== undefined && raw !== null) {
          const parsed = parseProductTourAccountState(raw);
          if (!parsed) {
            setStateLoadFailed(true);
            return;
          }
          setState(parsed);
        }
      })
      .catch(() => {
        if (!cancelled) setStateLoadFailed(true);
      })
      .finally(() => {
        if (!cancelled) setStateReady(true);
      });

    return () => { cancelled = true; };
  }, [settingKey, user?.id]);

  const persistOutcome = useCallback(async (
    status: ProductTourStateStatus,
    autoStartDisabled: boolean,
  ): Promise<void> => {
    if (!definition || !settingKey) return;
    const nextState: ProductTourAccountState = {
      schemaVersion: 1,
      tourVersion: definition.version,
      status,
      autoStartDisabled: autoStartDisabled || Boolean(state?.autoStartDisabled),
    };
    await axios.put(
      resolveCoreApiPath(`/api/user-settings/${encodeURIComponent(settingKey)}`),
      { value: nextState },
    );
    setState(nextState);
    patchUserSettings({ [settingKey]: nextState });
  }, [definition, patchUserSettings, settingKey, state?.autoStartDisabled]);

  const start = useCallback(async (): Promise<boolean> => {
    if (!definition || !labels) return false;
    const currentNonce = ++startNonceRef.current;
    requestProductTourSurface(null);
    if (activeDriverRef.current) {
      outcomeHandledRef.current = true;
      activeDriverRef.current.destroy();
    }
    activeDriverRef.current = null;

    const revealControllerAvailable = isVisibleProductTourElement(
      document.querySelector(productTourTargetSelector('user-profile')),
    );
    const visibleSteps = definition.steps.filter((step) => {
      if (!isProductTourStepAuthorized(step, user?.permissions, Boolean(user?.isAdmin))) return false;
      const selector = productTourTargetSelector(step.target);
      if (!selector) return false;
      if (isVisibleProductTourElement(document.querySelector(selector))) return true;
      return Boolean(step.reveal && revealControllerAvailable);
    });
    if (visibleSteps.length === 0) return false;

    const { driver } = await import('driver.js');
    if (currentNonce !== startNonceRef.current) return false;
    await prepareProductTourSurface(visibleSteps[0]?.reveal);
    if (currentNonce !== startNonceRef.current) {
      requestProductTourSurface(null);
      return false;
    }
    outcomeHandledRef.current = false;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const documentDirection = window.getComputedStyle(document.documentElement).direction;

    let transitionPending = false;
    const moveToPreparedStep = (tourDriver: Driver, nextIndex: number) => {
      if (transitionPending || nextIndex < 0 || nextIndex >= visibleSteps.length) return;
      transitionPending = true;
      void prepareProductTourSurface(visibleSteps[nextIndex]?.reveal)
        .then(() => {
          if (tourDriver.isActive()) tourDriver.moveTo(nextIndex);
        })
        .finally(() => {
          transitionPending = false;
        });
    };

    const instance = driver({
      animate: !prefersReducedMotion,
      duration: 240,
      overlayColor: resolveOverlayColor(),
      overlayOpacity: 0.72,
      smoothScroll: !prefersReducedMotion,
      allowClose: true,
      allowScroll: true,
      stagePadding: 8,
      stageRadius: 10,
      disableActiveInteraction: true,
      skipMissingElement: true,
      waitForElement: 600,
      allowKeyboardControl: true,
      popoverClass: 'qp-product-tour-popover',
      popoverOffset: 12,
      showButtons: ['next', 'previous', 'close'],
      showProgress: true,
      progressText: labels.progress,
      nextBtnText: labels.next,
      prevBtnText: labels.previous,
      doneBtnText: labels.done,
      steps: visibleSteps.map((step) => ({
        element: productTourTargetSelector(step.target),
        skipMissingElement: true,
        waitForElement: 600,
        popover: {
          title: escapeProductTourText(step.title),
          description: escapeProductTourText(step.description),
          side: step.side,
          align: resolveProductTourAlignmentForDirection(
            step.side,
            step.align,
            documentDirection,
          ),
        },
      })),
      onNextClick: (_element, _step, options) => {
        moveToPreparedStep(options.driver, (options.index ?? 0) + 1);
      },
      onPrevClick: (_element, _step, options) => {
        moveToPreparedStep(options.driver, (options.index ?? 0) - 1);
      },
      onPopoverRender: (popover) => {
        popover.wrapper.setAttribute('role', 'dialog');
        popover.wrapper.setAttribute('aria-modal', 'true');
        popover.wrapper.setAttribute('data-qp-interaction-surface', 'product-tour');

        popover.closeButton.style.removeProperty('display');
        popover.closeButton.classList.add('modal-close', 'modal-header-btn');
        popover.closeButton.setAttribute('aria-label', labels.close);
        popover.closeButton.title = labels.close;
        const mountedCloseIcon = closeIconMountRef.current;
        if (mountedCloseIcon?.container === popover.closeButton) {
          mountedCloseIcon.root.render(<Icon name="close" size={20} />);
        } else {
          releaseCloseIconRoot();
          const closeIconRoot = createRoot(popover.closeButton);
          closeIconRoot.render(<Icon name="close" size={20} />);
          closeIconMountRef.current = { container: popover.closeButton, root: closeIconRoot };
        }

        popover.footer.style.display = 'grid';
        popover.previousButton.style.removeProperty('display');
        popover.previousButton.classList.add(
          'btn-secondary',
          'qubepro-form-action',
          'qubepro-form-action-compact',
        );
        popover.nextButton.style.removeProperty('display');
        popover.nextButton.classList.add(
          'btn-primary',
          'qubepro-form-action',
          'qubepro-form-action-compact',
        );

        const neverButton = document.createElement('button');
        neverButton.type = 'button';
        neverButton.className = 'qp-product-tour-never-button qubepro-form-tertiary-action';
        neverButton.textContent = labels.neverShowAgain;
        neverButton.title = labels.neverShowAgain;
        neverButton.addEventListener('click', () => {
          if (neverButton.disabled) return;
          neverButton.disabled = true;
          outcomeHandledRef.current = true;
          requestProductTourSurface(null);
          void persistOutcome('dismissed', true)
            .then(() => instance.destroy())
            .catch(() => {
              outcomeHandledRef.current = false;
              neverButton.disabled = false;
            });
        });
        popover.footer.prepend(neverButton);
      },
      onCloseClick: () => {
        outcomeHandledRef.current = true;
        requestProductTourSurface(null);
        void persistOutcome('dismissed', false).finally(() => instance.destroy());
      },
      onDoneClick: () => {
        outcomeHandledRef.current = true;
        requestProductTourSurface(null);
        void persistOutcome('completed', false).finally(() => instance.destroy());
      },
      onDestroyed: () => {
        requestProductTourSurface(null);
        releaseCloseIconRoot();
        activeDriverRef.current = null;
        if (!outcomeHandledRef.current) {
          outcomeHandledRef.current = true;
          void persistOutcome('dismissed', false);
        }
      },
    });

    activeDriverRef.current = instance;
    instance.drive();
    return true;
  }, [definition, labels, persistOutcome, releaseCloseIconRoot, user?.isAdmin, user?.permissions]);

  useImperativeHandle(ref, () => ({ start }), [start]);

  useEffect(() => {
    if (
      !definition
      || !labels
      || !stateReady
      || stateLoadFailed
      || !user?.id
      || !shouldAutoStartProductTour(definition, state)
    ) return;
    const timer = window.setTimeout(() => { void start(); }, AUTO_START_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [definition, start, state, stateLoadFailed, stateReady, user?.id]);

  useEffect(() => () => {
    startNonceRef.current += 1;
    outcomeHandledRef.current = true;
    releaseCloseIconRoot();
    activeDriverRef.current?.destroy();
    activeDriverRef.current = null;
  }, [releaseCloseIconRoot]);

  return null;
});

export default ProductTourHost;
